import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Footer from "../components/Footer";
import { api, downloadUrl } from "../lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [order, setOrder] = useState(null);
  const [message, setMessage] = useState("Verifying your payment…");
  const attemptsRef = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setMessage("Missing payment session.");
      return;
    }
    const poll = async () => {
      if (stopped.current) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        const ps = r.data.payment_status;
        const st = r.data.status;
        if (ps === "paid") {
          stopped.current = true;
          setOrder(r.data.order);
          setStatus("paid");
          setMessage("Payment successful. Your download is ready.");
          return;
        }
        if (st === "expired") {
          stopped.current = true;
          setStatus("expired");
          setMessage("This payment session has expired. Please try again.");
          return;
        }
        if (attemptsRef.current >= 8) {
          stopped.current = true;
          setStatus("failed");
          setMessage("We couldn't confirm your payment yet. Check your email or use the recover link.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attemptsRef.current >= 8) {
          stopped.current = true;
          setStatus("failed");
          setMessage("Could not verify payment.");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stopped.current = true; };
  }, [sessionId]);

  return (
    <div className="App">
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
        {status === "polling" ? (
          <>
            <p className="text-[0.7rem] tracking-[0.32em] uppercase" style={{ color: "var(--opus-sage)" }}>Processing</p>
            <h1 className="font-display mt-4" style={{ fontSize: "clamp(2.2rem, 5vw, 3.4rem)", fontWeight: 500 }} data-testid="success-polling">{message}</h1>
            <div className="mt-10 inline-block w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: "var(--opus-sage)", borderTopColor: "transparent" }} />
          </>
        ) : status === "paid" ? (
          <>
            <p className="text-[0.7rem] tracking-[0.32em] uppercase" style={{ color: "var(--opus-sage)" }}>Thank you</p>
            <h1 className="font-display mt-4" style={{ fontSize: "clamp(2.4rem, 5.5vw, 3.6rem)", fontWeight: 500 }} data-testid="success-paid">Your resource is ready.</h1>
            <span className="opus-rule mt-6 inline-block" />
            <p className="font-body italic mt-7 leading-relaxed" style={{ color: "var(--opus-text-secondary)", fontSize: "1.1rem" }}>
              {order?.product_title ? <>You purchased <em>{order.product_title}</em>.</> : "Your purchase is complete."}
              {" "}A copy of this confirmation is on file under <strong>{order?.customer_email}</strong>.
            </p>
            {order?.download_token ? (
              <a
                href={downloadUrl(order.download_token)}
                data-testid="download-link"
                className="btn-sage inline-flex items-center gap-2 px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase mt-10"
                style={{ borderRadius: "2px" }}
              >
                Download now ↓
              </a>
            ) : null}
            <div className="mt-12">
              <Link to="/" data-testid="back-to-library" className="text-[0.7rem] tracking-[0.3em] uppercase underline underline-offset-[6px]" style={{ color: "var(--opus-sage)" }}>
                Back to the library
              </Link>
            </div>
          </>
        ) : (
          <>
            <p className="text-[0.7rem] tracking-[0.32em] uppercase" style={{ color: "#A0392C" }}>Issue</p>
            <h1 className="font-display mt-4" style={{ fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 500 }} data-testid="success-failed">{message}</h1>
            <div className="mt-10 flex gap-4 justify-center">
              <Link to="/recover" className="btn-sage px-6 py-3 text-[0.72rem] tracking-[0.3em] uppercase" style={{ borderRadius: "2px" }}>Recover purchase</Link>
              <Link to="/" className="text-[0.7rem] tracking-[0.3em] uppercase underline self-center" style={{ color: "var(--opus-sage)" }}>Back to library</Link>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
